import { useCallback, useState } from 'react';

import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';

import { EditServiceData } from '../../../Services.types';
import { useCreateServiceQuery, useUpdateServiceQuery } from '../queries';
import { SERVICE_MODAL_MODE } from '../ServiceModal.constants';
import { CreateServiceFormValues } from '../ServiceModal.types';

interface UseServiceSubmitParams {
  mode: string;
  editData?: EditServiceData | null;
  onSuccess: (serviceName: string) => void;
  onClose: () => void;
}

const useServiceSubmit = ({ mode, editData, onSuccess, onClose }: UseServiceSubmitParams) => {
  const { handleCreate, isLoading: isCreateLoading } = useCreateServiceQuery();
  const { handleUpdate, isLoading: isUpdateLoading } = useUpdateServiceQuery();
  const { handleAddPermanentSnackbar } = useSnackbar();

  const [isServerError, setServerError] = useState(false);

  const isLoading = mode === SERVICE_MODAL_MODE.EDIT ? isUpdateLoading : isCreateLoading;

  const handleSubmit = useCallback(
    async (values: CreateServiceFormValues) => {
      try {
        if (mode === SERVICE_MODAL_MODE.EDIT && editData) {
          await handleUpdate(editData.id, {
            name: values.name.trim(),
            categoryType: values.categoryType,
          });
          handleAddPermanentSnackbar({
            text: `${values.name.trim()} service has been updated`,
            closeIcon: true,
            state: SnackbarState.positive,
          });
          onClose();
        } else {
          await handleCreate({
            name: values.name.trim(),
            categoryType: values.categoryType,
          });
          onSuccess(values.name.trim());
        }
      } catch {
        setServerError(true);
      }
    },
    [mode, editData, handleCreate, handleUpdate, handleAddPermanentSnackbar, onClose, onSuccess]
  );

  return { handleSubmit, isLoading, isServerError, setServerError };
};

export default useServiceSubmit;
